import {Alert, Box, Button, Chip, CircularProgress, Paper, Typography} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import MyTextField from "../../../../components/MyTextField";
import UserSearchPicker from "../UserSearchPicker";

// Step 1 — Login lookup. The manager enters a login and explicitly checks availability. When the
// login is free a Setup Method section appears (Start from scratch selected; Clone deferred to
// FE-7). When taken, the existing user's identity is shown with placeholder actions.
const Step1Login = (ctx) => {
    const {
        draft, setField, loginStatus, existingUser, checking, loginError, checkLogin, resetLogin, onEditUser,
        selectSetupMethod, cloneFromUser, cloneLoading, cloneError, cloneSummary,
    } = ctx;

    const onLoginChange = (value) => {
        // Editing the login after a check invalidates the previous result.
        if (loginStatus !== "unchecked") resetLogin(true);
        setField("login", value);
    };

    return (
        <Box sx={{maxWidth: 640, mx: "auto"}}>
            <Box sx={{display: "flex", alignItems: "flex-start", gap: 1}}>
                <Box sx={{flex: 1}}>
                    <MyTextField
                        value={draft.login}
                        editable={loginStatus !== "taken"}
                        label="Login"
                        type="text"
                        required={true}
                        onChange={(e) => onLoginChange(e.target.value)}
                    />
                </Box>
                <Button
                    variant="contained"
                    sx={{mt: 1, whiteSpace: "nowrap"}}
                    disabled={!draft.login || checking || loginStatus === "taken"}
                    onClick={checkLogin}
                >
                    {checking ? <><CircularProgress size={16} sx={{mr: 1}} /> Checking…</> : "Check"}
                </Button>
            </Box>

            {loginError && <Alert severity="error" sx={{mt: 2}}>{loginError}</Alert>}

            {loginStatus === "available" && (
                <>
                    <Alert icon={<CheckCircleOutlineIcon fontSize="inherit" />} severity="success" sx={{mt: 2}}>
                        <b>{draft.login}</b> is available.
                    </Alert>

                    <Typography sx={{mt: 3, mb: 1, fontWeight: 700, fontSize: "0.8rem", textTransform: "uppercase", letterSpacing: "0.06em", color: "text.secondary"}}>
                        Setup Method
                    </Typography>
                    <Box sx={{display: "flex", gap: 2}}>
                        <SetupCard
                            selected={draft.setupMethod === "scratch"}
                            title="Start from scratch"
                            description="Manually configure all settings in each step."
                            onClick={() => selectSetupMethod("scratch")}
                        />
                        <SetupCard
                            selected={draft.setupMethod === "clone"}
                            title="Clone existing user"
                            description="Copy classification, groups, and territories from another user."
                            onClick={() => selectSetupMethod("clone")}
                        />
                    </Box>

                    {draft.setupMethod === "clone" && (
                        <Box sx={{mt: 2}}>
                            <UserSearchPicker showTerritoryCount onSelect={cloneFromUser} />
                            {cloneLoading && (
                                <Box sx={{display: "flex", alignItems: "center", gap: 1, mt: 2}}>
                                    <CircularProgress size={16} />
                                    <Typography sx={{fontSize: "0.85rem", color: "text.secondary"}}>Loading user…</Typography>
                                </Box>
                            )}
                            {cloneError && !cloneLoading && (
                                <Alert severity="error" sx={{mt: 2}}>Couldn't load that user. Please try another.</Alert>
                            )}
                            {cloneSummary && !cloneLoading && (
                                <Alert icon={<CheckCircleOutlineIcon fontSize="inherit" />} severity="info" sx={{mt: 2}}>
                                    Cloned from <b>{cloneSummary.sourceName || "user"}</b>: {cloneSummary.userTypeName || "—"}
                                    {cloneSummary.businessUnitCode ? ` · ${cloneSummary.businessUnitCode}` : ""} ·{" "}
                                    {cloneSummary.groupNames.length} group{cloneSummary.groupNames.length === 1 ? "" : "s"}
                                    {cloneSummary.groupNames.length > 0 ? ` (${cloneSummary.groupNames.join(", ")})` : ""} ·{" "}
                                    {cloneSummary.territoryCount} territor{cloneSummary.territoryCount === 1 ? "y" : "ies"}. You can
                                    adjust everything in the next steps.
                                </Alert>
                            )}
                        </Box>
                    )}
                </>
            )}

            {loginStatus === "taken" && existingUser && (
                <Paper variant="outlined" sx={{mt: 2, p: 2}}>
                    <Box sx={{display: "flex", alignItems: "center", gap: 1, mb: 1}}>
                        <Typography sx={{fontWeight: 700}}>
                            {[existingUser.firstName, existingUser.lastName].filter(Boolean).join(" ") || draft.login}
                        </Typography>
                        <Chip
                            size="small"
                            label={existingUser.activated ? "Active" : "Inactive"}
                            color={existingUser.activated ? "success" : "default"}
                            variant="outlined"
                        />
                    </Box>
                    <Typography sx={{fontSize: "0.85rem", color: "text.secondary"}}>
                        {(existingUser.userType?.displayName || existingUser.repType?.name || existingUser.role || "—")}
                        {existingUser.businessUnitCode ? ` · ${existingUser.businessUnitCode}` : ""}
                    </Typography>
                    <Typography sx={{fontSize: "0.85rem", color: "text.secondary", mt: 0.5}}>
                        This login already exists — you can't create a new user with it.
                    </Typography>
                    <Box sx={{display: "flex", gap: 1, mt: 2}}>
                        <Button variant="contained" onClick={() => onEditUser(existingUser.id)}>Edit This User</Button>
                        <Button onClick={() => resetLogin(false)}>Enter Different Login</Button>
                    </Box>
                </Paper>
            )}
        </Box>
    );
};

const SetupCard = ({selected, disabled, title, description, onClick}) => (
    <Paper
        variant="outlined"
        onClick={disabled ? undefined : onClick}
        sx={{
            flex: 1,
            p: 2,
            cursor: disabled ? "not-allowed" : "pointer",
            opacity: disabled ? 0.5 : 1,
            borderColor: selected ? "secondary.main" : "divider",
            borderWidth: selected ? 2 : 1,
            backgroundColor: selected ? "rgba(0, 163, 255, 0.06)" : "white",
        }}
    >
        <Typography sx={{fontWeight: 700, fontSize: "0.9rem"}}>{title}</Typography>
        <Typography sx={{fontSize: "0.8rem", color: "text.secondary", mt: 0.5}}>{description}</Typography>
    </Paper>
);

export default Step1Login;
